// signal-evidence — pure parsers shared by Market & Client views.

const hostnameOf = (u) => {
    if (!u || typeof u !== "string") return null;
    try {
        return new URL(u).hostname.replace(/^www\./, "");
    } catch {
        return u;
    }
};

const PUBLISHER_OVERRIDES = {
    "businessinsider.com": "Business Insider",
    "markets.businessinsider.com": "Markets · Business Insider",
    "msn.com": "MSN",
    "prnewswire.com": "PR Newswire",
    "globenewswire.com": "GlobeNewswire",
    "theglobeandmail.com": "The Globe and Mail",
    "eqs-news.com": "EQS News",
    "dailycoin.com": "DailyCoin",
    "crypto-reporter.com": "Crypto Reporter",
    "coindesk.com": "CoinDesk",
    "cointelegraph.com": "Cointelegraph",
    "theblock.co": "The Block",
    "bloomberg.com": "Bloomberg",
    "reuters.com": "Reuters",
    "ft.com": "Financial Times",
    "wsj.com": "Wall Street Journal",
    "thefintechtimes.com": "The Fintech Times",
    "thedefiant.io": "The Defiant",
    "finance.yahoo.com": "Yahoo Finance",
    "nsjonline.com": "North State Journal",
};

const publisherLabelFromHost = (host) => host ? (PUBLISHER_OVERRIDES[host] || host) : null;

/** Trailing-truncation marker test: detects whether a narrative string ends
 *  with an ellipsis ("...", "…") or an explicit "[truncated]" tag (case
 *  insensitive). Centralised here so every consumer agrees on the same
 *  truncation signal — e.g. the Market Track Detail Panel's
 *  `Read full weekly framing →` guard reuses this. */
export const TRAILING_TRUNCATION_RE = /(\.{3}|…|\[truncated\])\s*$/i;

/** True when the candidate string is a non-empty, non-truncated narrative.
 *  `null`/`undefined`/empty strings → false. Trailing ellipsis or
 *  `[truncated]` tag → false. */
export const summaryAppearsComplete = (raw) => {
    if (typeof raw !== "string") return false;
    const trimmed = raw.trim();
    if (!trimmed) return false;
    return !TRAILING_TRUNCATION_RE.test(trimmed);
};

/** Parse a "Name (n) | Name (m) | …" distribution string into a sorted
 *  [{ name, count }] list. Tolerates extra whitespace, missing counts (defaults
 *  to 0), and pre-split arrays. */
export const parseDistribution = (raw) => {
    if (!raw) return [];
    let items;
    if (Array.isArray(raw)) {
        items = raw;
    } else if (typeof raw === "string") {
        items = raw.split("|").map((s) => s.trim()).filter(Boolean);
    } else {
        return [];
    }
    const out = [];
    for (const it of items) {
        if (typeof it === "string") {
            const m = it.match(/^(.+?)\s*\((\d+)\)\s*$/);
            if (m) {
                out.push({ name: m[1].trim(), count: parseInt(m[2], 10) });
            } else if (it.trim()) {
                out.push({ name: it.trim(), count: 0 });
            }
        } else if (it && typeof it === "object") {
            const name = it.name || it.label || it.entity;
            const count = typeof it.count === "number" ? it.count : (typeof it.mentions === "number" ? it.mentions : 0);
            if (name) out.push({ name: String(name), count });
        }
    }
    return out.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
};

/** Normalise an upstream source entry (object or string) into a uniform shape. */
const normaliseSource = (raw) => {
    if (!raw) return null;
    if (typeof raw === "string") {
        const host = hostnameOf(raw);
        return { url: raw, host, publisher: publisherLabelFromHost(host), title: null, publishedAt: null, relevanceScore: null };
    }
    if (typeof raw !== "object") return null;
    const url = raw.url || raw.URL || raw.link || raw.normalized_url || null;
    if (!url) return null;
    const host = hostnameOf(url);
    const titleRaw = raw.title || raw.headline;
    const title = typeof titleRaw === "string" && titleRaw.trim() ? titleRaw.trim() : null;
    return {
        url,
        host,
        publisher: raw.publisher || raw.source || publisherLabelFromHost(host),
        title,
        publishedAt: raw.publishedAt || raw.date || raw.published_at || raw.published || null,
        relevanceScore: typeof raw.relevanceScore === "number" ? raw.relevanceScore : (typeof raw.relevance === "number" ? raw.relevance : null),
        sourceType: raw.sourceType || raw.source_type || null,
        companyCount: typeof raw.companyCount === "number" ? raw.companyCount : null,
        assetClassCount: typeof raw.assetClassCount === "number" ? raw.assetClassCount : null,
        blockchainCount: typeof raw.blockchainCount === "number" ? raw.blockchainCount : null,
    };
};

/** Pick the best available source list off a signal/trackcard, preferring
 *  `sources.byRelevance` style structured fields, then byDate, then retrieved
 *  articles, then the legacy `TopSourcesJSON` list. Returns
 *  { primary: [...], secondary: [...] } where `primary` is the default-shown
 *  list and `secondary` is what an "all sources" expansion should add. */
export const collectSourceLists = (signal) => {
    if (!signal) return { primary: [], secondary: [], primaryLabel: null };
    const pick = (...keys) => {
        for (const k of keys) {
            const path = k.split(".");
            let v = signal;
            for (const p of path) v = v ? v[p] : undefined;
            if (Array.isArray(v) && v.length) return v;
        }
        return null;
    };
    const byRelevance = pick("sourcesByRelevance", "sources.byRelevance", "SourcesByRelevance", "SourcesByRelevanceJSON");
    const byDate = pick("sourcesByDate", "sources.byDate", "SourcesByDate", "SourcesByDateJSON");
    const retrieved = pick("retrievedArticleList", "sources.retrievedArticles", "RetrievedArticles", "RetrievedArticlesJSON");
    const legacy = pick("topSources", "TopSourcesJSON", "TopSources");
    const sourceDetails = pick("sourceDetails");

    const seen = new Set();
    const dedupe = (xs) => {
        const out = [];
        for (const x of xs || []) {
            const n = normaliseSource(x);
            if (!n || !n.url) continue;
            if (seen.has(n.url)) continue;
            seen.add(n.url);
            out.push(n);
        }
        return out;
    };
    const primary = dedupe(byRelevance || retrieved || legacy || sourceDetails);
    const secondary = dedupe([...(byDate || []), ...(retrieved || []), ...(legacy || []), ...(sourceDetails || [])]);
    return {
        primary,
        secondary,
        primaryLabel: byRelevance ? "By relevance" : (retrieved ? "Retrieved articles" : (legacy ? "Top sources" : null)),
    };
};

/** Defensive guard that decides whether the raw dropped-companies list should
 *  be shown as a normal weekly movement insight, vs. a caution note. */
export const movementGuard = (signal) => {
    if (!signal) return { ok: false };
    const flag = (signal.movementQualityFlag || signal.MovementQualityFlag || "").toUpperCase();
    const note = signal.movementQualityNote || signal.MovementQualityNote || null;
    if (flag && flag !== "NORMAL") {
        return { ok: false, flag, note };
    }
    const dropped = Array.isArray(signal.droppedCompanies) ? signal.droppedCompanies.length : (Array.isArray(signal.DroppedCompanies) ? signal.DroppedCompanies.length : 0);
    const prev = signal.previousCompanyCount;
    if (typeof prev === "number" && prev > 0 && dropped > prev) {
        return {
            ok: false,
            flag: "IMPLAUSIBLE",
            note: "More dropped companies than were observed last week — the comparison basis may have been low-coherence; interpret cautiously.",
        };
    }
    return { ok: true, flag: flag || "NORMAL", note };
};

/** Resolve display-safe new / dropped lists per the spec: prefer the explicit
 *  Display fields, fall back to raw new/dropped lists, gated by movementGuard. */
export const resolveMovementLists = (signal) => {
    if (!signal) return { newCompanies: [], droppedCompanies: [], showDropped: false, guard: { ok: true } };
    const guard = movementGuard(signal);
    const display = signal.displayNewCompanies || signal.DisplayNewCompanies;
    const displayDropped = signal.displayDroppedCompanies || signal.DisplayDroppedCompanies;
    if (Array.isArray(display) || Array.isArray(displayDropped)) {
        return {
            newCompanies: Array.isArray(display) ? display : [],
            droppedCompanies: Array.isArray(displayDropped) ? displayDropped : [],
            showDropped: true,
            guard,
            displaySafe: true,
        };
    }
    const newCo = Array.isArray(signal.newCompanies) ? signal.newCompanies : (Array.isArray(signal.NewCompanies) ? signal.NewCompanies : []);
    const droppedCo = Array.isArray(signal.droppedCompanies) ? signal.droppedCompanies : (Array.isArray(signal.DroppedCompanies) ? signal.DroppedCompanies : []);
    return {
        newCompanies: newCo,
        droppedCompanies: droppedCo,
        showDropped: guard.ok,
        guard,
        displaySafe: false,
    };
};

export { hostnameOf, publisherLabelFromHost, normaliseSource };

// ---------------------------------------------------------------------------
// Evidence-chip helpers (Market View — Executive Read, Top Signals, Detail).
//
// These read the new structured evidence fields shipped in `signals-history.json`
// without rewriting existing field usage. Everything is defensive — missing
// fields return empty arrays so the UI hides chips silently.
// ---------------------------------------------------------------------------

const _hasText = (v) => typeof v === "string" && v.trim().length > 0;
const _arr = (v) => (Array.isArray(v) ? v : []);
const _cleanList = (arr) => _arr(arr).map((x) => (typeof x === "string" ? x.trim() : x)).filter((x) => x != null && x !== "");

/** Safely parse a JSON-string field. Returns the parsed value or undefined. */
const _safeJson = (raw) => {
    if (!_hasText(raw)) return undefined;
    try {
        return JSON.parse(raw);
    } catch {
        return undefined;
    }
};

/** Pick the first non-empty array among candidates. */
const _firstNonEmptyArr = (...cands) => {
    for (const c of cands) {
        if (Array.isArray(c) && c.length > 0) return c;
    }
    return [];
};

/** Split a pipe-separated string ("A | B | C") into a trimmed array. */
export const parsePipeList = (raw) => {
    if (Array.isArray(raw)) return _cleanList(raw);
    if (!_hasText(raw)) return [];
    return raw.split("|").map((s) => s.trim()).filter(Boolean);
};

/** Normalise a source-link record to `{url, domain, title, date}`. Accepts the
 *  structured `claim.evidence.sourceLinks[i]` shape, a plain URL string, or a
 *  loose object with `url|link|href` fields. Returns null when no URL. */
export const normaliseSourceLink = (raw) => {
    if (!raw) return null;
    if (typeof raw === "string") {
        const url = raw.trim();
        if (!url) return null;
        return { url, domain: hostnameOf(url), title: null, date: null };
    }
    if (typeof raw !== "object") return null;
    const url = _hasText(raw.url) ? raw.url.trim()
        : _hasText(raw.link) ? raw.link.trim()
        : _hasText(raw.href) ? raw.href.trim()
        : _hasText(raw.normalized_url) ? raw.normalized_url.trim()
        : null;
    if (!url) return null;
    const domain = _hasText(raw.domain) ? raw.domain.trim() : hostnameOf(url);
    const title = _hasText(raw.title) ? raw.title.trim() : null;
    const date = _hasText(raw.date) ? raw.date.trim() : (_hasText(raw.publishedAt) ? raw.publishedAt.trim() : null);
    return { url, domain: domain || null, title, date };
};

/** Normalise a structured-evidence object (the value at
 *  `weeklyBriefing.evidenceLayer.bigPicture`, or the `claim.evidence` nested
 *  under a structured key-takeaway / implication / watch item). Returns a
 *  shape-stable `{examples, companies, locations, exampleUrls, sourceLinks,
 *  sourceCount, exampleCount}` regardless of which casing the upstream used. */
export const normaliseEvidence = (raw) => {
    if (!raw || typeof raw !== "object") {
        return { examples: [], companies: [], companyTypes: [], assetClasses: [], locations: [], exampleUrls: [], sourceLinks: [], sourceCount: 0, exampleCount: 0 };
    }
    const examples = _cleanList(raw.examples);
    const companies = _cleanList(raw.companies);
    const companyTypes = _firstNonEmptyArr(_cleanList(raw.companyTypes), _cleanList(raw.company_types));
    const assetClasses = _firstNonEmptyArr(_cleanList(raw.assetClasses), _cleanList(raw.asset_classes));
    const locations = _cleanList(raw.locations);
    const exampleUrls = _firstNonEmptyArr(_cleanList(raw.exampleUrls), _cleanList(raw.example_urls));
    const sourceLinks = _arr(raw.sourceLinks).map(normaliseSourceLink).filter(Boolean);
    const sourceCount = Number.isFinite(raw.sourceCount) ? raw.sourceCount
        : (sourceLinks.length || exampleUrls.length);
    const exampleCount = Number.isFinite(raw.exampleCount) ? raw.exampleCount : examples.length;
    return { examples, companies, companyTypes, assetClasses, locations, exampleUrls, sourceLinks, sourceCount, exampleCount };
};

/** Read a "claim" entry from a structured evidence array. Accepts BOTH:
 *  - the structured-claim wrapper `{text, examples, evidence: {...}}` used by
 *    `evidenceLayer.keyTakeaways / marketStructureImplications / watchItems`
 *  - a flat evidence object that already has `examples / companies / sourceLinks`
 *    (the shape used by `evidenceLayer.bigPicture` / `executiveSummary`).
 *  Returns `{text, evidence: <normalised>}`. */
export const readClaimEvidence = (claim) => {
    if (!claim || typeof claim !== "object") return { text: null, evidence: normaliseEvidence(null) };
    const text = _hasText(claim.text) ? claim.text.trim() : null;
    // Detect the flat-evidence shape (no `.evidence` nest but has any evidence-bearing keys).
    const looksFlat = !claim.evidence && (
        Array.isArray(claim.examples) || Array.isArray(claim.companies)
        || Array.isArray(claim.sourceLinks) || Array.isArray(claim.exampleUrls)
        || Array.isArray(claim.example_urls) || Array.isArray(claim.locations)
    );
    const source = claim.evidence && typeof claim.evidence === "object" ? claim.evidence : (looksFlat ? claim : null);
    const evidence = normaliseEvidence(source);
    // For flat-shape callers the "claim.examples" might also live at the top
    // level — merge them in if `evidence.examples` is empty.
    if (evidence.examples.length === 0 && Array.isArray(claim.examples)) {
        evidence.examples = _cleanList(claim.examples);
        if (!evidence.exampleCount) evidence.exampleCount = evidence.examples.length;
    }
    return { text, evidence };
};

/** Read evidence off a `TopSignalsJSON[]` entry. Prefers the JSON variants and
 *  falls back to pipe-string fields. Source-count is derived from URL count
 *  when available, then example count. Returns the same shape as `normaliseEvidence`. */
export const readTopSignalEvidence = (topSig) => {
    if (!topSig || typeof topSig !== "object") return normaliseEvidence(null);
    const examplesJ = _safeJson(topSig.EvidenceExamplesJSON);
    const companiesJ = _safeJson(topSig.EvidenceCompaniesJSON);
    const locationsJ = _safeJson(topSig.EvidenceLocationsJSON);
    const exampleUrlsJ = _safeJson(topSig.EvidenceExampleURLsJSON);
    const examples = _firstNonEmptyArr(_cleanList(examplesJ), parsePipeList(topSig.EvidenceExamples));
    const companies = _firstNonEmptyArr(_cleanList(companiesJ), parsePipeList(topSig.EvidenceCompanies));
    const locations = _firstNonEmptyArr(_cleanList(locationsJ), parsePipeList(topSig.EvidenceLocations));
    const exampleUrls = _firstNonEmptyArr(_cleanList(exampleUrlsJ), parsePipeList(topSig.EvidenceExampleURLs));
    const sourceLinks = exampleUrls.map((u) => normaliseSourceLink(u)).filter(Boolean);
    return {
        examples, companies, locations, exampleUrls, sourceLinks,
        sourceCount: exampleUrls.length || examples.length,
        exampleCount: examples.length,
    };
};

/** Read `signal.latestWeeklyEvidence` (per the new market-track shape) into a
 *  normalised bundle. Returns null when absent. `topCompanies` etc. are
 *  preserved as-is; `evidenceSources` is normalised to `{url,domain,title,date}`. */
export const readLatestWeeklyEvidence = (signalOrTrack) => {
    if (!signalOrTrack || typeof signalOrTrack !== "object") return null;
    const lwe = signalOrTrack.latestWeeklyEvidence;
    if (!lwe || typeof lwe !== "object") return null;
    const evidenceSources = _arr(lwe.evidenceSources).map(normaliseSourceLink).filter(Boolean);
    const topSources = _arr(lwe.topSources).map(normaliseSourceLink).filter(Boolean);
    return {
        summary: _hasText(lwe.summary) ? lwe.summary : null,
        whyItMatters: _hasText(lwe.whyItMatters) ? lwe.whyItMatters : null,
        hasExpandedText: lwe.hasExpandedText === true,
        hasFullText: lwe.hasFullText === true,
        summaryAppearsTruncated: lwe.summaryAppearsTruncated === true,
        whyItMattersAppearsTruncated: lwe.whyItMattersAppearsTruncated === true,
        expandedTextSource: lwe.expandedTextSource || null,
        structuralSignalMatched: lwe.structuralSignalMatched === true,
        topCompanies: _cleanList(lwe.topCompanies),
        topCompanyTypes: _cleanList(lwe.topCompanyTypes),
        topAssetClasses: _cleanList(lwe.topAssetClasses),
        topBlockchains: _cleanList(lwe.topBlockchains),
        topLocations: _cleanList(lwe.topLocations),
        sourceCount: Number.isFinite(lwe.sourceCount) ? lwe.sourceCount : (evidenceSources.length || topSources.length),
        evidenceSources,
        topSources,
    };
};

/** Pick up to `max` example chips from a normalised evidence object following
 *  the documented priority. Chips longer than `maxChipLen` characters (default
 *  40) are dropped from the compact row — they remain available in expanded
 *  evidence detail / source cards if surfaced separately. Priority chain:
 *  `examples` → `companies` (up to 3) → `companyTypes` (short ones only) →
 *  `assetClasses` → `locations` (up to 2). Generic single-word use-case labels
 *  like "Payments & Transfers" are deprioritised. Returns `[]` if nothing. */
export const pickExampleChips = (ev, max = 2, maxChipLen = 40) => {
    const isGeneric = (s) => {
        if (!_hasText(s)) return false;
        const t = s.toLowerCase();
        return t === "payments & transfers" || t === "payments and transfers" || t === "use cases" || t === "general";
    };
    const isCompact = (s) => _hasText(s) && s.trim().length <= maxChipLen;
    const merge = (...lists) => {
        const seen = new Set();
        const out = [];
        const generic = [];
        for (const list of lists) {
            for (const item of list || []) {
                if (!_hasText(item)) continue;
                if (!isCompact(item)) continue; // drop long sentence-like chips
                const k = item.trim().toLowerCase();
                if (seen.has(k)) continue;
                seen.add(k);
                if (isGeneric(item)) generic.push(item.trim());
                else out.push(item.trim());
                if (out.length >= max) return out;
            }
        }
        while (out.length < max && generic.length > 0) out.push(generic.shift());
        return out;
    };
    if (!ev || typeof ev !== "object") return [];
    return merge(
        ev.examples,
        (ev.companies || []).slice(0, 3),
        ev.companyTypes,
        ev.assetClasses,
        (ev.locations || []).slice(0, 2),
    );
};

/** Pick the best available list of source cards from a claim-evidence bundle
 *  (already normalised via `normaliseEvidence`). Priority: sourceLinks →
 *  exampleUrls. Returns `[{url,domain,title,date}]`. */
export const pickClaimSources = (ev) => {
    if (!ev || typeof ev !== "object") return [];
    if (Array.isArray(ev.sourceLinks) && ev.sourceLinks.length > 0) return ev.sourceLinks;
    if (Array.isArray(ev.exampleUrls) && ev.exampleUrls.length > 0) {
        return ev.exampleUrls.map(normaliseSourceLink).filter(Boolean);
    }
    return [];
};

/** Pick source cards for the Signal Detail panel: `evidenceSources` first,
 *  then `topSources`. Both are already normalised by `readLatestWeeklyEvidence`. */
export const pickLatestEvidenceSources = (lwe) => {
    if (!lwe || typeof lwe !== "object") return [];
    if (Array.isArray(lwe.evidenceSources) && lwe.evidenceSources.length > 0) return lwe.evidenceSources;
    if (Array.isArray(lwe.topSources) && lwe.topSources.length > 0) return lwe.topSources;
    return [];
};

/** True when there is anything visible to render in an evidence-chip block. */
export const hasAnyEvidenceChip = (ev, opts = {}) => {
    if (!ev || typeof ev !== "object") return false;
    const sources = opts.sources != null ? opts.sources : (ev.sourceCount || 0);
    const chips = pickExampleChips(ev, 3);
    return sources > 0 || chips.length > 0;
};
